/**
 * usePanelState Hook
 *
 * Local state management for panel UI behaviors.
 * Handles persistence and animations.
 */

import { useState, useCallback, useEffect } from 'react';

import type { PanelState, PanelSize } from '../types';

interface PanelStateOptions {
  /** Key for localStorage persistence */
  storageKey?: string;
  /** Default panel state */
  defaultState?: PanelState;
  /** Default panel size */
  defaultSize?: PanelSize;
  /** Whether to persist to localStorage */
  persist?: boolean;
}

interface PanelStateResult {
  /** Current panel state */
  state: PanelState;
  /** Current panel size */
  size: PanelSize;
  /** Set panel state */
  setState: (state: PanelState) => void;
  /** Set panel size */
  setSize: (size: PanelSize) => void;
  /** Toggle panel state */
  toggle: () => void;
  /** Expand panel */
  expand: () => void;
  /** Collapse panel */
  collapse: () => void;
  /** Cycle through sizes */
  cycleSize: () => void;
}

const STORAGE_KEY_STATE = 'solomon-panel-state';
const STORAGE_KEY_SIZE = 'solomon-panel-size';

export function usePanelState({
  storageKey,
  defaultState = 'collapsed',
  defaultSize = 'medium',
  persist = false,
}: PanelStateOptions = {}): PanelStateResult {
  // Initialize from localStorage if persist is enabled
  const [state, setStateInternal] = useState<PanelState>(() => {
    if (persist && typeof window !== 'undefined') {
      const key = storageKey ? `${storageKey}-state` : STORAGE_KEY_STATE;
      const stored = localStorage.getItem(key);
      if (stored === 'collapsed' || stored === 'expanded') {
        return stored;
      }
    }
    return defaultState;
  });

  const [size, setSizeInternal] = useState<PanelSize>(() => {
    if (persist && typeof window !== 'undefined') {
      const key = storageKey ? `${storageKey}-size` : STORAGE_KEY_SIZE;
      const stored = localStorage.getItem(key);
      if (stored === 'small' || stored === 'medium' || stored === 'large') {
        return stored;
      }
    }
    return defaultSize;
  });

  // Persist state changes
  useEffect(() => {
    if (persist && typeof window !== 'undefined') {
      const key = storageKey ? `${storageKey}-state` : STORAGE_KEY_STATE;
      localStorage.setItem(key, state);
    }
  }, [state, persist, storageKey]);

  // Persist size changes
  useEffect(() => {
    if (persist && typeof window !== 'undefined') {
      const key = storageKey ? `${storageKey}-size` : STORAGE_KEY_SIZE;
      localStorage.setItem(key, size);
    }
  }, [size, persist, storageKey]);

  const setState = useCallback((newState: PanelState) => {
    setStateInternal(newState);
  }, []);

  const setSize = useCallback((newSize: PanelSize) => {
    setSizeInternal(newSize);
  }, []);

  const toggle = useCallback(() => {
    setStateInternal((prev) => (prev === 'collapsed' ? 'expanded' : 'collapsed'));
  }, []);

  const expand = useCallback(() => {
    setStateInternal('expanded');
  }, []);

  const collapse = useCallback(() => {
    setStateInternal('collapsed');
  }, []);

  const cycleSize = useCallback(() => {
    setSizeInternal((prev) => {
      const sizes: PanelSize[] = ['small', 'medium', 'large'];
      const currentIndex = sizes.indexOf(prev);
      const nextIndex = (currentIndex + 1) % sizes.length;
      return sizes[nextIndex];
    });
  }, []);

  return {
    state,
    size,
    setState,
    setSize,
    toggle,
    expand,
    collapse,
    cycleSize,
  };
}

export default usePanelState;
